import React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { createUser } from '../../slices/userSlice';
import { ChevronDown, X } from 'react-feather';
import { formatPhoneNumberInput } from '../../hooks/phoneFormat';

export default function NewUser({ onClose }) {
	const dispatch = useDispatch();
	const {
		register,
		handleSubmit,
		setValue,
		formState: { errors, isSubmitting },
	} = useForm();

	const onSubmit = async (data) => {
		await dispatch(
			createUser({
				email: data?.email,
				password: data?.password,
				firstName: data?.firstName,
				lastName: data?.lastName,
				phoneNumber: data?.phoneNumber,
				role: data?.role,
				metadata: {},
			})
		);
		onClose();
	};

	return (
		<div
			className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
			onClick={(event) => {
				if (event.target !== event.currentTarget) return;
				onClose();
			}}
		>
			<div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl">
				<div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
					<div>
						<h2 className="text-xl font-bold text-slate-900">Create User</h2>
						<p className="mt-1 text-sm text-slate-500">
							Add a team member and assign their role.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
						aria-label="Close"
					>
						<X size={20} />
					</button>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className="px-6 py-6">
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<div className="md:col-span-2">
							<label
								className="block text-sm font-semibold text-slate-700 mb-1"
								htmlFor="new-user-email"
							>
								Email
							</label>
							<input
								id="new-user-email"
								{...register('email', { required: 'Email is required' })}
								placeholder="name@company.com"
								className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 outline-none transition focus:ring-2 focus:ring-blue-500"
							/>
							{errors.email && (
								<p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
							)}
						</div>

						<div>
							<label
								className="block text-sm font-semibold text-slate-700 mb-1"
								htmlFor="new-user-firstname"
							>
								First name
							</label>
							<input
								id="new-user-firstname"
								{...register('firstName', { required: 'First Name is required' })}
								placeholder="First name"
								className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 outline-none transition focus:ring-2 focus:ring-blue-500"
							/>
							{errors.firstName && (
								<p className="mt-1 text-sm text-red-600">
									{errors.firstName.message}
								</p>
							)}
						</div>

						<div>
							<label
								className="block text-sm font-semibold text-slate-700 mb-1"
								htmlFor="new-user-lastname"
							>
								Last name
							</label>
							<input
								id="new-user-lastname"
								{...register('lastName', { required: 'Last Name is required' })}
								placeholder="Last name"
								className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 outline-none transition focus:ring-2 focus:ring-blue-500"
							/>
							{errors.lastName && (
								<p className="mt-1 text-sm text-red-600">
									{errors.lastName.message}
								</p>
							)}
						</div>

						<div>
							<label
								className="block text-sm font-semibold text-slate-700 mb-1"
								htmlFor="new-user-phone"
							>
								Phone
							</label>
							<input
								id="new-user-phone"
								placeholder="(123) 456-7890"
								{...register('phoneNumber', {
									required: 'Phone Number is required',
									pattern: {
										value: /^\(\d{3}\) \d{3}-\d{4}$/,
										message:
											'Phone number must be in the format (123) 456-7890',
									},
									onChange: (event) => {
										const formatted = formatPhoneNumberInput(event.target.value);
										setValue('phoneNumber', formatted);
									},
								})}
								className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 outline-none transition focus:ring-2 focus:ring-blue-500"
							/>
							{errors.phoneNumber && (
								<p className="mt-1 text-sm text-red-600">
									{errors.phoneNumber.message}
								</p>
							)}
						</div>

						<div>
							<label
								className="block text-sm font-semibold text-slate-700 mb-1"
								htmlFor="new-user-role"
							>
								Role
							</label>
							<div className="relative">
								<select
									id="new-user-role"
									{...register('role', { required: 'Role is required' })}
									className="block w-full appearance-none rounded-lg border border-slate-200 bg-white px-3 py-2 pr-10 text-slate-900 outline-none transition focus:ring-2 focus:ring-blue-500"
								>
									<option value="">Select role</option>
									<option value="INSTALLER">Installer</option>
									<option value="SALESMAN">Dealer</option>
									<option value="EMPLOYEE">Employee</option>
									<option value="ADMIN">Admin</option>
									<option value="SUPER">Super</option>
								</select>
								<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-500">
									<ChevronDown size={18} />
								</div>
							</div>
							{errors.role && (
								<p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
							)}
						</div>

						<div className="md:col-span-2">
							<label
								className="block text-sm font-semibold text-slate-700 mb-1"
								htmlFor="new-user-password"
							>
								Password
							</label>
							<input
								id="new-user-password"
								{...register('password', { required: 'Password is required' })}
								placeholder="Set a temporary password"
								type="password"
								className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 outline-none transition focus:ring-2 focus:ring-blue-500"
							/>
							{errors.password && (
								<p className="mt-1 text-sm text-red-600">
									{errors.password.message}
								</p>
							)}
						</div>
					</div>

					<div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
						<button
							type="button"
							onClick={onClose}
							className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={isSubmitting}
							className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
						>
							{isSubmitting ? 'Creating...' : 'Create user'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
